import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Bell } from 'lucide-react';
import {
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader,
  Textarea
} from '@bliks-io/ui';
import { ErrorCode } from '@bliks-io/contracts';
import { useNudge } from '@/api/mutations/useNudge';

interface NudgeDialogProps {
  onClose: () => void;
  targetUserId: string;
  targetName: string;
  processId: string;
  processName: string;
  stepId?: string;
}

export function NudgeDialog({
  onClose,
  targetUserId,
  targetName,
  processId,
  processName,
  stepId
}: NudgeDialogProps) {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const nudgeMutation = useNudge();

  const handleSend = () => {
    if (!message.trim()) return;

    nudgeMutation.mutate(
      {
        targetUserId,
        processId,
        stepId,
        message: message.trim()
      },
      {
        onSuccess: () => {
          toast.success(t('recordingDashboard.nudge.success'));
          setMessage('');
          onClose();
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : '';
          toast.error(
            message === ErrorCode.NUDGE_TARGET_NOT_ACTIVE
              ? t(ErrorCode.NUDGE_TARGET_NOT_ACTIVE)
              : t('common.errorOccurred')
          );
        }
      }
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogShell size="xs">
        <DialogShellHeader
          icon={<Bell className="h-5 w-5" />}
          title={t('recordingDashboard.nudge.title')}
          subtitle={t('recordingDashboard.nudge.description', {
            name: targetName,
            process: processName
          })}
          onClose={onClose}
        />
        <DialogShellBody>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('recordingDashboard.nudge.messagePlaceholder')}
            rows={4}
            maxLength={500}
          />
        </DialogShellBody>
        <DialogShellFooter
          secondary={{
            label: t('common.cancel'),
            onClick: onClose
          }}
          primary={{
            label: t('recordingDashboard.nudge.send'),
            onClick: handleSend,
            disabled: !message.trim() || nudgeMutation.isPending,
            loading: nudgeMutation.isPending
          }}
        />
      </DialogShell>
    </Dialog>
  );
}
